import { supabase } from './supabase'
import { fetchAll } from './fetchall'
import { LANE_FROM, REPLY_TO, localDate, render, type Audience, type Lane } from './campaigns'
import type { Agent } from './types'

/**
 * Putting an email into the outbox — the only writes that lead to a send.
 *
 * Nothing here sends anything. Both lanes write rows that *wait for Renee*:
 *
 *   REVIEWED  one `outreach_drafts` row per recipient. She opens each in Gmail
 *             and presses send there, then tells the app it went. The app never
 *             touches her Real Brokerage mailbox — see `email-scope.md`, "two
 *             doors, neither ours".
 *
 *   BLAST     one `campaigns` row, switched OFF. A deployed Function with the
 *             service key is what would turn an approved campaign into
 *             `campaign_sends` and call Resend; it does not exist yet, and
 *             until it does nothing here writes a row any sender could find.
 *             A browser must never hold a credential that can email 785 people.
 *
 * So the worst thing a bug in this file can do is put a card in front of her
 * that she did not expect. That is the line the whole design is built on and it
 * is why this file is allowed to exist before the sender does.
 */

/** `outreach_drafts` is keyed `(agent_id, trigger, occurrence)` — that unique
 * constraint IS the idempotency. Two clicks on Send cannot double-send, because
 * the second insert collides rather than adding a row. */
export const MANUAL_TRIGGER = 'manual'

/**
 * Triggers `outreach_log` will actually accept a row for — mirrors its own
 * check constraint (`20260822200000_outreach_log_manual_trigger.sql`), not
 * `format.ts`'s `TRIGGER_LABELS` (which exists for display, and carries
 * display-only aliases like `firstclosing` that have no matching constraint
 * value).
 *
 * `departed` belongs here for the same reason the other five milestone
 * triggers do — `milestones.ts` writes real `trigger: 'departed'` drafts,
 * and twelve such rows already existed in `outreach_log` (found when the
 * 2026-08-22 migration's own CHECK constraint tightened and rejected them —
 * the original constraint had simply omitted `departed` since the table was
 * created, an oversight the migration also fixed).
 *
 * `welcome` is deliberately NOT here, matching `sync-metabase.ts`'s own
 * choice to drop welcome rows from what it writes to `outreach_log`: a
 * welcome draft skipped or deleted from the Outbox must not leave a
 * permanent record of outreach for a joiner nobody actually drafted or
 * sent to.
 *
 * A `drip:<uuid>` trigger is also NOT here. Drips aren't live yet, and
 * whether/how a drip touch should write to this dedupe ledger is its own
 * design question — not something to guess into place here.
 */
const OUTREACH_LOG_TRIGGERS = new Set<string>([
  'capped', 'elite', 'anniversary', 'birthday', 'first_closing', 'departed', MANUAL_TRIGGER,
])

/**
 * THERE USED TO BE A `sendableFromOutbox(trigger)` HERE.
 *
 * Built 2026-08-21, the parallel-run day itself: `loadQueue` reads every
 * `outreach_drafts` row with no trigger filter, so a milestone draft
 * (birthday, anniversary, and the rest — the exact category the OLD
 * dashboard was still sending during the parallel run) sat in the Outbox
 * with a live Send button, next to the `manual` reviewed sends this screen
 * is actually meant for. Sending it from here too would have emailed the
 * same agent twice, once from each system. The fix at the time restricted
 * Send/Copy to `trigger === MANUAL_TRIGGER` and left everything else
 * visible for Skip/Delete only — the fix doc's own words: "the gate is
 * meant to be temporary... revisit at cutover, once the old dashboard is
 * retired, and let it return true for everything."
 *
 * Removed 2026-08-23, per Chris: "Remove the temporary, bring this to full
 * finished ready to use state." Knowingly ahead of the old dashboard
 * actually being retired — Chris, confirming the trade-off: "That's fine. I
 * am the only one in here right now and will not go through the entire send
 * process. Just need to test it to the end." See
 * `outbox-milestone-gate-removal-flagged-2026-08-23.md`. A function that
 * always returned `true` would have been a trap for the next reader who
 * assumed it still gated something, so it is gone rather than neutered.
 */

export type OutboxResult = {
  /** What actually landed, counted from what the database returned. */
  created: number
  /** Rows that collided with something already queued — not an error. */
  alreadyQueued: number
  error: string | null
  /** For a blast: the campaign row now holding it. */
  campaignId?: string
  /**
   * The id of the row that was created, when `queueReviewed` inserted
   * exactly one — i.e. a single-recipient send. Not populated for a
   * multi-recipient batch: nothing reads "the one id" for a batch, and
   * returning only the last-inserted row's id there would invite a caller to
   * treat it as if it meant something for the whole group. Added 2026-08-23
   * for the single-recipient Gmail-bypass shortcut in `Email.tsx`, which
   * needs the real row back to mark it sent via `markDraft` without a second
   * read.
   */
  id?: number
}

function readable(message: string): string {
  const m = message.toLowerCase()
  if (m.includes('permission denied') || m.includes('jwt') || m.includes('not authorized')) {
    return 'Your sign-in expired. Refresh the page and sign in again — nothing was queued.'
  }
  return message
}

const CHUNK = 100

type DbError = { message: string; code?: string }

/**
 * A unique-constraint violation. `23505` is the Postgres code and is the thing
 * to test; the wording is a fallback, because PostgREST has not always passed
 * the code through and a message match is not something to rely on alone.
 */
const isDuplicate = (e: DbError) =>
  e.code === '23505' || /duplicate key|unique constraint/i.test(e.message)

/** The request never got an answer, as opposed to getting a "no". */
const isNetwork = (e: DbError) => {
  /**
   * A Postgres error code means the database answered — so it is a definite
   * "no", whatever the wording. `57014` is a statement timeout and `25P02` an
   * aborted transaction; both read as network trouble and are the opposite of
   * it. Only a reply that never arrived has no code at all.
   */
  if (e.code) return false
  return /failed to fetch|networkerror|network request failed|load failed|timeout|aborted/i.test(e.message)
}

/**
 * A slug that identifies this particular batch.
 *
 * It goes in `occurrence`, which is half the uniqueness key, so two different
 * sends to the same person are distinct rows while a double-click on the same
 * one is not. Derived from the subject and the moment, both of which the caller
 * already has — no clock is read in here, so the tests can pin it.
 *
 * The date is local. It is only an identifier, so UTC would not have been
 * *wrong* — but this string is the one a person reads when two batches have to
 * be told apart in the database, and a batch she queued at 8pm Sunday filed
 * under Monday is a trap laid for whoever looks next.
 *
 * **THE DAY IS THE WHOLE POINT. It used to carry hours, minutes and seconds,
 * and that made the uniqueness key useless.** Every press of "Yes, queue it"
 * produced a key nothing had ever seen, so `ON CONFLICT DO NOTHING` never
 * conflicted with anything. The failure that mattered: 785 recipients, the
 * connection drops after 300, the screen correctly says "300 of 785 were
 * queued before this stopped" — she presses the button again five seconds
 * later, and those 300 are queued a *second* time. 300 duplicate cards, and
 * the screen reports 785 created and none already queued, because from the
 * key's point of view this was a brand new job. Nothing anywhere says
 * otherwise. The only duplicate the old key could catch was a double-click
 * inside the same second, and the button is disabled while the write is in
 * flight, so it could not catch even that.
 *
 * On the day, the retry finds the 300 and inserts only the 485 that are
 * missing — which is what a person means when they press it again.
 *
 * **The accepted cost, decided by Chris on 2026-08-17:** the identical subject
 * cannot be queued to the same person twice in one day. The second attempt
 * reports them as already queued rather than making a second card. Deliberate:
 * the same email twice in a day is far more often the mistake than the intent,
 * and the wording is different every real time it happens.
 *
 * This also retires an old worry. Seconds were once justified by the hour that
 * repeats every November when the clocks go back — local wall-clock time is
 * ambiguous there. A date has no such hour, so the ambiguity is gone rather
 * than worked around.
 */
export function batchKey(subject: string, at: Date): string {
  const slug = subject.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)
  return `${localDate(at).replace(/-/g, '')}-${slug || 'email'}`
}

/**
 * Queue a reviewed email: one card per recipient, each already filled in.
 *
 * The merge fields are rendered NOW rather than when she opens the card, so
 * what she reviews is exactly what she sends. Rendering at send time would mean
 * the roster could change underneath a card she has already read.
 */
export async function queueReviewed(opts: {
  audience: Audience
  subject: string
  body: string
  label: string
  at: Date
  reviewedBy?: string | null
  /**
   * Where these rows are filed, for callers that are not the composer.
   *
   * A drip campaign passes `drip:<id>` and `s<step>`, which is how one touch
   * becomes one draft per agent and — because `(agent_id, trigger, occurrence)`
   * is unique — physically cannot reach the same agent twice, however many
   * times the tab is opened or the button pressed.
   *
   * Defaulted rather than required so the composer is untouched: a one-off
   * send is still `manual` filed under the day's batch key.
   */
  trigger?: string
  occurrence?: string
}): Promise<OutboxResult> {
  const trigger = opts.trigger ?? MANUAL_TRIGGER
  const occurrence = opts.occurrence ?? batchKey(opts.subject, opts.at)
  // `email` here is the one `addressFor` already validated and trimmed. Reading
  // `agent.email` again would put an untrimmed copy in `facts.to`, and the
  // hand-off check compares the two — so an address that had a stray space
  // would report itself as "changed since this was queued" on every card.
  const rows = opts.audience.recipients.map(({ agent, email }) => ({
    agent_id: agent.rezen_id,
    trigger,
    occurrence,
    subject: render(opts.subject, agent).text,
    body: render(opts.body, agent).text,
    why: opts.label,
    label: opts.label,
    facts: { lane: 'reviewed' as Lane, from: LANE_FROM.reviewed, reply_to: REPLY_TO, to: email },
    status: 'pending' as const,
  }))

  /**
   * REFUSE A DIFFERENT MESSAGE UNDER A SUBJECT ALREADY USED TODAY.
   *
   * This check exists because of the day-level key above, and without it that
   * key is dangerous rather than merely strict. `ignoreDuplicates` keeps the
   * row that is already there, so:
   *
   *   09:00  "Licence renewals" to 40 — the body has the wrong date in it.
   *   09:20  she spots it, fixes the body, keeps the subject, sends again.
   *
   * Every row collides, nothing is written, and the screen would say "all 40
   * were already waiting" — which she reads as *already handled*. The 40 cards
   * still hold the wrong date and nothing anywhere says her correction was
   * thrown away. Duplicating them, which is what the old second-level key did,
   * was bad but at least visible and at least contained the right words.
   *
   * The blast lane has had exactly this guard from the start (`queueBlast`
   * below, "it says something different"). The reviewed lane never needed one
   * until its key started spanning presses.
   *
   * Compared per agent on the rendered text, because that is what she will
   * actually read — a template change that renders identically for everyone in
   * the audience is not a change worth stopping for.
   *
   * A read failure here does NOT block the write. This is a guard against a
   * silent overwrite, not part of the send; refusing to queue anything because
   * a safety read timed out would be the worse failure of the two.
   */
  const ids = rows.map((r) => r.agent_id)
  const existing = new Map<string, { subject: string; body: string }>()
  for (let i = 0; i < ids.length; i += CHUNK) {
    const { data, error } = await supabase
      .from('outreach_drafts')
      .select('agent_id,subject,body')
      .eq('trigger', trigger)
      .eq('occurrence', occurrence)
      .in('agent_id', ids.slice(i, i + CHUNK))
    if (error) break
    for (const row of (data ?? []) as { agent_id: string; subject: string; body: string }[]) {
      existing.set(row.agent_id, { subject: row.subject, body: row.body })
    }
  }
  const changed = rows.filter((r) => {
    const was = existing.get(r.agent_id)
    return was !== undefined && (was.subject !== r.subject || was.body !== r.body)
  })
  if (changed.length > 0) {
    return {
      created: 0,
      alreadyQueued: 0,
      error:
        `Nothing was queued. An email with this subject line already went into the Outbox earlier today, ` +
        `and this one says something different — so saving it would have thrown away ${
          changed.length === 1 ? 'the change' : 'your changes'
        } without a word. ` +
        `Change the subject line and send it as its own email, or delete the earlier batch from the Outbox first.`,
    }
  }

  let created = 0
  /**
   * Collisions are counted per chunk rather than worked out at the end.
   *
   * `rows.length - created` is only right when every chunk ran. On a partial
   * failure this used to report `alreadyQueued: 0` regardless — so a second
   * attempt where chunks 1-3 collided entirely and chunk 5 died said "100
   * emails are waiting" over "100 of 785 were queued", reading as 685 missing
   * when 385 were. Newly reachable, because collisions are ordinary now.
   */
  let collided = 0
  // The id of the single row created, IF this call creates exactly one. Tracked
  // as rows land rather than read back afterward — `rows.length === 1` alone
  // isn't enough, because a single-row call can still collide (0 created) and
  // must not hand back a stale/foreign id in that case.
  let onlyId: number | undefined
  for (let i = 0; i < rows.length; i += CHUNK) {
    const batch = rows.slice(i, i + CHUNK)
    const { data, error } = await supabase
      .from('outreach_drafts')
      .upsert(batch, { onConflict: 'agent_id,trigger,occurrence', ignoreDuplicates: true })
      .select('id')
    if (error) {
      return {
        created,
        alreadyQueued: collided,
        error:
          created === 0
            ? `Nothing was queued. ${readable(error.message)}`
            : `${created} of ${rows.length} were queued before this stopped. ${readable(error.message)}`,
      }
    }
    // Count what came back, not what was sent. `ON CONFLICT DO NOTHING` returns
    // only the rows that were really inserted, which is the strongest answer
    // available to "did this land".
    const landed = (data ?? []) as { id: number }[]
    created += landed.length
    collided += batch.length - landed.length
    if (landed.length === 1) onlyId = landed[0].id
  }

  return {
    created,
    alreadyQueued: collided,
    error: null,
    // Only meaningful when exactly one row was created across the whole call —
    // a multi-chunk send that happens to land one-per-chunk must not report the
    // last chunk's id as if it spoke for the batch.
    ...(created === 1 && onlyId !== undefined ? { id: onlyId } : {}),
  }
}

/**
 * Save a blast — one row, in one table, that no sender can act on.
 *
 * WHY THERE IS NO `campaign_occurrences` ROW
 *
 * There is no sender yet. The Function with the service key that would turn an
 * approved occurrence into 785 emails does not exist, and neither does the
 * screen on which Renee would approve one. Until both do, an occurrence row is
 * a thing sitting in the table a future sender will scan, with
 * `status = 'queued'` — which is exactly the value a first draft of that
 * sender will select on. The check constraint permits nothing weaker, so the
 * only guard available would have been a comment asking whoever writes it to
 * join to `campaigns` and check `is_enabled` too.
 *
 * A comment is not a guard. So the occurrence is simply not written. A blast is
 * one `campaigns` row with `is_enabled = false`, there is nothing in
 * `campaign_occurrences` for any query to find, and the day the approval screen
 * and the sender arrive is the day writing occurrences starts to mean
 * something. Removing a table from the blast radius beats annotating it.
 *
 * `campaigns.id` carries the local date and the label, so it is the idempotency
 * key on its own — which is why nothing was lost by dropping the second row.
 *
 * IT IS READ BEFORE IT IS WRITTEN, AND NEVER OVERWRITTEN
 *
 * An upsert would replace a message she has already saved with a different one
 * under the same name and day, and report success — the primary key would never
 * fire, so nothing would ever say "you already saved this today". Same lesson
 * as the tag undo that would have deleted 386 rows.
 *
 * And a *different* message under a name already used today is refused rather
 * than quietly dropped. Writing the new occurrence while keeping the old
 * audience — which is what happened when these were two rows — meant the
 * confirm screen could say "Saved for 785 agents" over a stored list of 120.
 */
export async function queueBlast(opts: {
  audience: Audience
  campaignId: string
  name: string
  subject: string
  body: string
  audienceRule: Record<string, unknown>
  cadence?: Record<string, unknown>
  createdBy?: string | null
}): Promise<OutboxResult> {
  const { data: existing, error: readErr } = await supabase
    .from('campaigns')
    .select('id,name,subject,body')
    .eq('id', opts.campaignId)
    .limit(1)
  if (readErr) {
    return { created: 0, alreadyQueued: 0, error: `Nothing was queued. ${readable(readErr.message)}` }
  }

  const already = (existing ?? [])[0] as
    | { id: string; name: string; subject: string; body: string }
    | undefined

  if (already) {
    const same = already.subject === opts.subject && already.body === opts.body
    if (same) {
      // The double-click, or the retry after a lost response. Nothing to do,
      // and nothing to worry her with.
      return { created: 0, alreadyQueued: 1, error: null, campaignId: already.id }
    }
    /**
     * A different message under the same name on the same day.
     *
     * Silently keeping the old one and reporting success is how the confirm
     * screen came to say "Saved for 785" over a stored audience of 120. She is
     * the only one who can say which of the two she meant, so she is asked.
     */
    return {
      created: 0,
      alreadyQueued: 0,
      error:
        `There is already a bulk email saved today called "${opts.name}", and it says something ` +
        'different. Give this one another name so the two do not get mixed up.',
    }
  }

  const { error } = await supabase.from('campaigns').insert({
    id: opts.campaignId,
    name: opts.name,
    audience: opts.audienceRule,
    cadence: opts.cadence ?? {},
    subject: opts.subject,
    body: opts.body,
    // Not a stored intention that is one deploy away from being a send.
    is_enabled: false,
    ...(opts.createdBy ? { created_by: opts.createdBy } : {}),
  })

  if (error) {
    // Another tab won the race by a hair between the read and this insert.
    // The primary key caught it, which is what it is for.
    if (isDuplicate(error)) return { created: 0, alreadyQueued: 1, error: null }
    /**
     * A dropped connection is not the same as a rejected write.
     *
     * The row may well have committed and the answer been lost on the way back
     * — so "Nothing was queued" is a claim this code is not in a position to
     * make. Saying so, and saying what to do about it, beats being confidently
     * wrong in either direction. Pressing it again is safe: the read at the top
     * will find the row if it landed.
     */
    if (isNetwork(error)) {
      return {
        created: 0,
        alreadyQueued: 0,
        error:
          'The connection dropped before this could be confirmed, so it may or may not have saved. ' +
          'Press it again — if it says it was already saved today, then it did save.',
      }
    }
    return { created: 0, alreadyQueued: 0, error: `Nothing was queued. ${readable(error.message)}` }
  }

  return { created: 1, alreadyQueued: 0, error: null, campaignId: opts.campaignId }
}

// ------------------------------------------------------------- the queue itself

export type QueuedDraft = {
  id: number
  agent_id: string
  trigger: string
  occurrence: string
  subject: string
  body: string
  why: string | null
  label: string | null
  status: 'pending' | 'sent' | 'skipped' | 'failed'
  facts: Record<string, unknown>
  created_at: string
}

/**
 * Everything still waiting.
 *
 * Paged, for the same reason the tags are: PostgREST cuts a plain select at a
 * thousand rows and says nothing. A reviewed send to 700 agents plus a couple
 * of unfinished batches is already within sight of that, and the failure would
 * be cards that are simply not on the screen — the emails she never sent and
 * has no way to discover she has not sent.
 */
export async function loadQueue(): Promise<{ rows: QueuedDraft[] | null; error: string | null }> {
  /**
   * `created_at` alone is not a total order and cannot be the sort here.
   *
   * It defaults to `now()`, which is *transaction* time — so all 52 rows of one
   * batch insert carry the identical timestamp. Offset-paging a tie block that
   * spans a page boundary drops rows or repeats them, and a repeated row is two
   * React children with the same key: two cards for one email, and an invitation
   * to send it twice. `id` breaks every tie.
   *
   * **`rows` is null on failure, not empty.** Returning `[]` made the screen say
   * "0 waiting" and "Nothing waiting. Nice." underneath a red error, with 52
   * emails sitting in the database.
   */
  const { rows, error, truncated } = await fetchAll<QueuedDraft>(
    'outreach_drafts',
    '*',
    ['created_at', 'id'],
    ['status', 'pending'],
  )
  if (error) return { rows: null, error: readable(error) }
  if (truncated) {
    return { rows, error: 'Only part of the outbox could be read — there are more waiting than this.' }
  }
  return { rows, error: null }
}

export type HeldBulkSend = {
  id: string
  name: string
  subject: string
  audience: { agent_ids?: string[] } | null
  /**
   * Set only when `generate-scheduled.ts` produced this row from a recurring
   * campaign's own firing. Null for a one-off blast composed by hand in the
   * Email composer's "Same message to everybody" lane — both origins land in
   * `campaigns` and both are held here; this field is kept in case a card
   * ever wants to say "from a recurring campaign" versus "one-off", not
   * because it decides which rows this function returns (see `id.startsWith`
   * below, not this column, for that).
   */
  generated_from_id: string | null
}

/**
 * The other half of what "waiting in the Outbox" means, since 2026-08-22 —
 * every bulk send sitting in `campaigns` that has not gone out from
 * `Bulk.tsx` yet, whichever of the two ways it got there.
 *
 * **Both origins, one id shape.** `id.startsWith('manual-')` is the same test
 * `Bulk.tsx` itself already uses to answer "is this a held/bulk-world row" —
 * it matches a one-off blast composed by hand (`manual-<date>-<slug>`, from
 * `Email.tsx`'s `submit()`) exactly as well as a generated recurring
 * occurrence (also `manual-<date>-<slug>`, per `generate-scheduled.ts`'s own
 * comment). A recurring campaign's own TEMPLATE row uses a different prefix
 * (`sched-<slug>`) and is correctly excluded — only something actually ready
 * to send belongs here, not the standing rule that produces it.
 *
 * Named for what it now covers, not what it covered when it was built
 * 2026-08-22 for recurring campaigns alone (`loadScheduledWaiting`, then) —
 * broadened 2026-08-23 to close the gap Chris flagged: a self-composed bulk
 * send never had `generated_from_id` set, so it never surfaced here despite
 * the Email composer's own confirm screen promising it would. See
 * `outbox-shell-for-manual-bulk-flagged-2026-08-23.md`.
 *
 * Pulled out to its own function (2026-08-23) so `Outbox.tsx`'s own
 * `HeldBulkSends` section and `navcounts.ts`'s nav badge read the exact
 * same "still waiting" test — `occ?.status !== 'sent'`, the same one
 * `Bulk.tsx` falls back to once its own in-memory send progress clears —
 * rather than two copies that could quietly disagree about the count.
 */
export async function loadHeldBulkSends(): Promise<{ rows: HeldBulkSend[] | null; error: string | null }> {
  const [c, o] = await Promise.all([
    fetchAll<HeldBulkSend>('campaigns', 'id,name,subject,audience,generated_from_id', ['created_at', 'id']),
    fetchAll<{ campaign_id: string; status: string; last_stopped_kind: string | null }>(
      'campaign_occurrences',
      'campaign_id,status,last_stopped_kind',
      ['campaign_id', 'id'],
    ),
  ])
  if (c.error) return { rows: null, error: readable(c.error) }
  if (o.error) return { rows: null, error: readable(o.error) }
  /**
   * A campaign stops being "waiting for review" two ways now, not one:
   * finished (`sent`), or deliberately abandoned partway
   * (`last_stopped_kind === 'fatal'`, the same marker `Bulk.tsx`'s "Stop
   * this send" and `skipHeldBulkSend` below write). Added 2026-09-08, the
   * day a real stuck send ("Team Connect 9/8") sat here indefinitely
   * reading "783 agents, when you approve it" — true of the audience, not
   * of what was actually still going to happen — after being stopped by
   * hand in the database because no UI existed to do it. See
   * `claude/team-connect-9-8-send-stopped-2026-09-08.md`.
   */
  const occByCampaign = new Map(o.rows.map((r) => [r.campaign_id, r]))
  const rows = c.rows.filter((b) => {
    if (!b.id.startsWith('manual-')) return false
    const occ = occByCampaign.get(b.id)
    if (occ?.status === 'sent') return false
    if (occ?.last_stopped_kind === 'fatal') return false
    return true
  })
  return { rows, error: null }
}

/**
 * The Outbox's "Skip" button for a held bulk send — get it off today's list,
 * one of two honest ways depending on what has actually happened to it:
 *
 *   - **Nothing sent yet** (no `campaign_occurrences` row at all) — delete
 *     the campaign outright, same as pressing Delete on an unstarted
 *     campaign in `Bulk.tsx`. Nobody was contacted, so there is nothing to
 *     keep a record of.
 *   - **A send was started** — deleting would destroy `campaign_sends`, the
 *     only record of who already received it, so this marks the occurrence
 *     permanently stopped instead (`last_stopped_kind = 'fatal'`), the exact
 *     same write `Bulk.tsx`'s "Stop this send" makes and the same marker
 *     `run-bulk-send.ts`'s cron Worker already knows to skip forever. The
 *     campaign then reads "stopped" in Bulk, and can be hidden from there
 *     like any finished one — archived, not erased.
 *
 * Either branch removes it from `loadHeldBulkSends()`'s own count on the
 * next load, since both make it fail one of the two checks above.
 */
export async function skipHeldBulkSend(campaignId: string): Promise<string | null> {
  const { data: occRows, error: occErr } = await supabase
    .from('campaign_occurrences')
    .select('id')
    .eq('campaign_id', campaignId)
    .limit(1)
  if (occErr) return readable(occErr.message)
  if (occRows && occRows.length > 0) {
    const { error } = await supabase
      .from('campaign_occurrences')
      .update({ last_stopped_kind: 'fatal', last_stopped_at: new Date().toISOString() })
      .eq('campaign_id', campaignId)
    return error ? readable(error.message) : null
  }
  const { error } = await supabase.from('campaigns').delete().eq('id', campaignId)
  return error ? readable(error.message) : null
}

/**
 * Mark one card sent, or skipped.
 *
 * `sent` here means "she told us it went" — the app handed her a filled-in
 * Gmail window and cannot observe what happened in it. That is a weaker fact
 * than Resend confirming a recipient, and the log has to keep them apart or it
 * is claiming a certainty it does not have. `facts.confirmed_by_hand` is how.
 *
 * Takes the whole draft, not just its id — `agent_id`/`trigger`/`occurrence`
 * are what `outreach_log` needs below, and `QueuedDraft` already carries all
 * three, so there is nothing to look up.
 */
export async function markDraft(
  draft: Pick<QueuedDraft, 'id' | 'agent_id' | 'trigger' | 'occurrence'>,
  status: 'sent' | 'skipped',
  facts: Record<string, unknown>,
  reviewedBy?: string | null,
): Promise<string | null> {
  const { data, error } = await supabase
    .from('outreach_drafts')
    .update({
      status,
      sent_at: status === 'sent' ? new Date().toISOString() : null,
      facts: { ...facts, confirmed_by_hand: status === 'sent' },
      ...(reviewedBy ? { reviewed_by: reviewedBy } : {}),
    })
    .eq('id', draft.id)
    .select('id')
  if (error) return readable(error.message)
  /**
   * A zero-row UPDATE is a success with no error — the same trap
   * `saveDraftBody` below has a comment about, and this is the worse place for
   * it. The row can be missing because it was finished in another tab, or
   * deleted underneath her. Without asking for it back, **"It went" reports
   * success and records nothing**: an email a person actually sent, with no row
   * anywhere saying so.
   */
  if (!Array.isArray(data) || data.length === 0) {
    return status === 'sent'
      ? 'That card is no longer in the outbox, so the send was not recorded. It may have been ' +
          'finished in another window. Reload before doing anything else.'
      : 'That card is no longer in the outbox — it may have been finished in another window.'
  }
  /**
   * Record it on `outreach_log` too, where the agent record's "Outreach
   * history" actually reads from — the walkthrough-flagged bug (see the
   * migration adding `manual` to that table's trigger constraint). Upserted,
   * not inserted: a milestone trigger already has a row here (written by the
   * daily sync, status `baseline`/`drafted`), and this is what turns it into
   * `sent`/`skipped`; a manual send has no prior row, so the same upsert
   * creates one.
   *
   * A failure here is deliberately NOT surfaced as an error to her. The
   * `outreach_drafts` write above — the record she actually watches ("It
   * went" flips this card away) — already succeeded, and telling her the
   * send failed when the thing that failed is a second, best-effort ledger
   * would be a false alarm she cannot act on from here. Logged to the
   * console so it is not silent to whoever reads it next.
   */
  if (OUTREACH_LOG_TRIGGERS.has(draft.trigger)) {
    const { error: logError } = await supabase.from('outreach_log').upsert(
      {
        agent_id: draft.agent_id,
        trigger: draft.trigger,
        occurrence: draft.occurrence,
        status,
        ts: new Date().toISOString(),
      },
      { onConflict: 'agent_id,trigger,occurrence' },
    )
    if (logError) console.error('outreach_log upsert failed', logError)
  }
  return null
}

/**
 * Persist an edit she made on the card, at the moment it is handed to Gmail.
 *
 * **Written at hand-off, not at "It went".** The body is the thing that just
 * left the app — Gmail was opened with *her* version, not the stored one — and
 * a draft whose text differs from the text that was sent is a record that
 * quietly lies. This is the same rule as the address in `finish()`, which logs
 * the address actually used rather than the one the card was built from.
 *
 * Writing it here also means it survives the tab closing between opening Gmail
 * and confirming, which is precisely the interruption the Outbox exists to
 * tolerate.
 *
 * Only `body` is updated. The subject is not editable on the card, and widening
 * this to a general row update would let a future caller overwrite `status` —
 * the one column on this table a person is supposed to own.
 */
export async function saveDraftBody(id: number, body: string): Promise<string | null> {
  /**
   * `.select('id')` is not decoration — without it this cannot fail.
   *
   * PostgREST answers an UPDATE that matched **nothing** with a success and no
   * error: a row already finished in another tab, or an RLS policy that no
   * longer matches this session. Reported as saved, the card would then repaint
   * itself with the new text and clear the pending edit, so the screen would
   * show the edit as persisted while the row still held the old wording — a
   * failure with no symptom at all, which is the worst shape this project has.
   *
   * Asking for the row back turns "no error" into "no rows", which is a thing
   * that can be checked.
   */
  const { data, error } = await supabase
    .from('outreach_drafts')
    .update({ body })
    .eq('id', id)
    .select('id')
  if (error) return readable(error.message)
  if (!Array.isArray(data) || data.length === 0) {
    return 'That edit did not save — the card may have been finished in another window. Reload before sending it.'
  }
  return null
}

/**
 * Delete a whole batch, by id.
 *
 * **By id, not by occurrence.** The caller deletes the rows it is looking at
 * and nothing else. An `occurrence` filter would reach rows this screen never
 * loaded — a card finished in another tab, or one that arrived after the page
 * did — and "delete what is on screen" is the only promise a confirmation
 * dialog can honestly make.
 *
 * **Chunked**, because the ids travel in the query string and a 785-card batch
 * would build a URL long enough to be rejected somewhere between here and
 * Postgres. Same 100 as the inserts.
 *
 * **Counts what actually went**, via `.select()`. A delete that matched nothing
 * returns success and no error, exactly as the update did, and reporting "batch
 * deleted" over rows that are still there is the failure with no symptom.
 *
 * Skip and Delete are deliberately different things. Skip says *I read this and
 * decided not to send it* and keeps the row. Delete says *this batch should
 * never have existed* — nobody was contacted, so there is nothing to audit, and
 * a `skipped` row would record a decision she never made.
 */
export async function deleteBatch(
  ids: readonly number[],
): Promise<{ deletedIds: number[]; error: string | null }> {
  /**
   * **The ids that went, not how many.**
   *
   * A count forces the caller to guess *which* rows are gone, and the only
   * guess available is "the first N I asked for". That is wrong the moment a
   * chunk comes back short — a row already finished in another tab, a policy
   * that no longer matches, a retry after a lost response. The caller then
   * clears the wrong cards from the screen and leaves cards standing for rows
   * that no longer exist: press Send on one of those and a real email goes to a
   * real agent with no row left to record it.
   *
   * `.select('id')` was already being asked for. This keeps the answer.
   */
  const deletedIds: number[] = []
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK)
    const { data, error } = await supabase
      .from('outreach_drafts')
      .delete()
      .in('id', slice as number[])
      /**
       * **Pending only.** Without this, deleting a batch destroys rows that
       * have already been sent — two tabs is enough: she presses "It went" in
       * one, the other still lists the card as waiting, and Delete there wipes
       * the record of an email a real person received. Delete means "this batch
       * should never have existed", and a sent row is evidence that part of it
       * did.
       */
      .eq('status', 'pending')
      .select('id')
    if (error) {
      /**
       * A dropped connection is not a "no". The rows may be gone. Saying so is
       * the difference between reloading and pressing Delete again over rows
       * that already went — the same reasoning as `queueBlast` above.
       */
      if (isNetwork(error)) {
        return {
          deletedIds,
          error:
            'The connection dropped before this could be confirmed, so some of these may or may ' +
            'not have been deleted. Reload the page to see what is actually left.',
        }
      }
      return {
        deletedIds,
        error:
          deletedIds.length > 0
            ? `${deletedIds.length} of ${ids.length} were deleted before this stopped. ${readable(error.message)}`
            : readable(error.message),
      }
    }
    for (const row of (data ?? []) as { id: number }[]) deletedIds.push(row.id)
  }
  if (deletedIds.length < ids.length) {
    return {
      deletedIds,
      error:
        `${deletedIds.length} of ${ids.length} were deleted. The rest were already sent, skipped ` +
        'or gone — reload to see what is left.',
    }
  }
  return { deletedIds, error: null }
}

/**
 * Everything queued under one batch, so a half-finished job is findable.
 *
 * **Grouped on trigger + occurrence + label, not on `occurrence` alone.** That
 * was safe while `occurrence` carried the second: every press had its own key,
 * so a group was always exactly one press. On the day key it is not, and one
 * group could hold two unrelated jobs:
 *
 *   09:00  "Team call Thursday" to 12 Charleston leaders, labelled
 *          "Charleston call". She works through 8 of them.
 *   14:00  "Team call Thursday" to 15 Greenville leaders, labelled
 *          "Greenville call". Different people, so nothing collides — 15 new
 *          rows, same occurrence.
 *
 * One group of 19 appears, named "Charleston call", timestamped 09:00. Press
 * **Delete this batch** because the Greenville one was a mistake and the 4
 * Charleston cards she had not finished go with it. `deleteBatch` promises it
 * deletes the rows the caller is looking at and nothing else, and it keeps that
 * promise — the damage is done here, by handing it the wrong rows.
 *
 * `label` separates them, and it is the right discriminator rather than a
 * convenient one: it is the answer to "what is this for?", it is what the group
 * is named after, and two sends she thinks of as one job carry the same one.
 * `trigger` is in the key because a milestone draft that happened to generate
 * the string `YYYYMMDD-slug` would otherwise merge into a manual batch, and
 * milestone occurrences are generated outside this repo where nothing stops
 * that.
 */
export function groupByBatch(
  rows: QueuedDraft[],
): { key: string; label: string; queuedAt: string | null; rows: QueuedDraft[] }[] {
  const m = new Map<string, QueuedDraft[]>()
  for (const r of rows) {
    const k = `${r.trigger}\u0000${r.occurrence}\u0000${r.label || r.why || ''}`
    const list = m.get(k) ?? []
    list.push(r)
    m.set(k, list)
  }
  return [...m.entries()].map(([key, list]) => ({
    key,
    label: list[0]?.label || list[0]?.why || 'Emails',
    /**
     * When the batch was queued — the earliest row in it, not the latest.
     *
     * Every row of one insert shares a `created_at` (it is transaction time),
     * so these are normally identical and the choice looks academic. It is not:
     * a batch queued in two goes, where the second filled in agents the first
     * collided on, holds two timestamps, and the batch is as old as its oldest
     * card. Without this the screen showed two groups reading
     * `BIRTHDAY · 2 LEFT`, stacked, with nothing at all to tell them apart.
     */
    queuedAt: list.reduce<string | null>(
      (earliest, r) =>
        // Guarded, so the return matches the type it claims. `created_at` is
        // not null in the schema, but a row reaching here without one would
        // otherwise make this `undefined` while the signature promised
        // `string | null` — and the screen would print "Invalid Date".
        r.created_at && (earliest === null || r.created_at < earliest) ? r.created_at : earliest,
      null,
    ),
    rows: list,
  }))
}

export function agentsById(agents: Agent[] | null): Map<string, Agent> {
  return new Map((agents ?? []).map((a) => [a.rezen_id, a]))
}

/**
 * What the "already opened in Gmail" record should become after a queue read.
 *
 * Pure, and out here rather than inside the component, because it is the piece
 * whose failure mode is somebody sending thirty emails twice — and while it
 * lived in a `.tsx` file nothing could test it. Two rules, both learned the
 * hard way:
 *
 *   - **Only prune on a good read.** `hadError` true means the queue could not
 *     be listed, and pruning against an empty list wipes every card she has
 *     already handed over. She comes back to a screen offering "Send" on
 *     everything with nothing saying which ones went.
 *
 *   - **Merge, never replace.** Two tabs each hold their own set; writing one
 *     over the other erases the other tab's work.
 */
export function nextHandedOff(opts: {
  /** This tab's set. */
  memory: ReadonlySet<number>
  /** What is in the shared store right now. */
  stored: ReadonlySet<number>
  /** The ids still waiting, or null when the read failed. */
  live: ReadonlySet<number> | null
}): Set<number> {
  const merged = new Set([...opts.memory, ...opts.stored])
  if (!opts.live) return merged
  return new Set([...merged].filter((id) => opts.live!.has(id)))
}

/**
 * Which cards have already been opened in Gmail, remembered across reloads.
 *
 * Moved here from `Outbox.tsx` 2026-08-23 so `Email.tsx`'s single-recipient
 * Gmail-bypass shortcut can mark a card handed-off the same way the Outbox
 * itself does, without a second copy of this bookkeeping drifting out of sync
 * with the first. Both callers read and write the one key.
 *
 * This was component state to begin with. A 52-card batch is not one sitting
 * — she closes the laptop, comes back, and every card is offering "Send"
 * again with no way to say which ones she already sent. Worse, the pile of
 * "did I do that one?" is exactly how a person ends up sending the same email
 * twice.
 *
 * `localStorage`, not `sessionStorage`: the gap here is measured in days.
 */
const HANDED_OFF_KEY = 'agent-care.outbox.handed-off'

export function readHandedOff(): Set<number> {
  try {
    const raw = window.localStorage.getItem(HANDED_OFF_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((n) => typeof n === 'number') : [])
  } catch {
    // A corrupt or unavailable store must not take the screen down with it.
    return new Set()
  }
}

/**
 * Written by merging with whatever is already stored, never by replacing it.
 *
 * Two tabs on the Outbox each hold their own in-memory set. Serialising this
 * one over the top of the other's would erase cards the other tab had already
 * handed off — so after a reload those cards offer "Send" again with no "It
 * went", which is precisely the "did I already do that one?" state this whole
 * mechanism exists to prevent, and the natural way out of it is to send twice.
 *
 * `prune` is the ids still waiting; anything else is dropped, so the record
 * cannot grow without limit.
 */
export function writeHandedOff(ids: Set<number>, prune?: Set<number>) {
  try {
    const merged = new Set([...readHandedOff(), ...ids])
    const kept = prune ? new Set([...merged].filter((id) => prune.has(id))) : merged
    window.localStorage.setItem(HANDED_OFF_KEY, JSON.stringify([...kept]))
  } catch {
    // Private browsing, a full quota — the screen still works, it just forgets.
  }
}

/** Taking one back off has to be a removal, not a merge, or it comes straight back. */
export function forgetHandedOff(id: number) {
  try {
    const next = readHandedOff()
    next.delete(id)
    window.localStorage.setItem(HANDED_OFF_KEY, JSON.stringify([...next]))
  } catch {
    // As above.
  }
}
